import { localStorageService } from "services/LocalStorage";
import { resolveEffectiveCurrency } from "services/pricing";

const SUPPORTED_DONATION_CURRENCIES = new Set(["USD", "EUR", "GBP"]);
const EUROPEAN_COUNTRY_CODES = new Set([
  "AT", "BE", "BG", "CH", "CY", "CZ", "DE", "DK", "EE", "ES", "FI", "FR", "GB", "GR", "HR",
  "HU", "IE", "IS", "IT", "LT", "LU", "LV", "MT", "NL", "NO", "PL", "PT", "RO", "SE", "SI",
  "SK",
]);

const normalizeCountryCode = (value) => {
  if (typeof value !== "string") {
    return "";
  }
  const normalizedCountryCode = value.toUpperCase().trim();
  return /^[A-Z]{2}$/.test(normalizedCountryCode) ? normalizedCountryCode : "";
};

const normalizeCurrencyCode = (value) => {
  if (typeof value !== "string") {
    return "";
  }
  const normalizedCurrencyCode = value.toUpperCase().trim();
  return /^[A-Z]{3}$/.test(normalizedCurrencyCode) ? normalizedCurrencyCode : "";
};

const normalizeDonateLink = (value) => {
  if (typeof value !== "string") {
    return "";
  }
  return value.trim();
};

const getConfiguredDonationLinks = () => ({
  USD: normalizeDonateLink(process.env.REACT_APP_STRIPE_DONATE_LINK_USD),
  EUR: normalizeDonateLink(process.env.REACT_APP_STRIPE_DONATE_LINK_EUR),
  GBP: normalizeDonateLink(process.env.REACT_APP_STRIPE_DONATE_LINK_GBP),
});

const resolveUserCountry = (user = null) => (
  normalizeCountryCode(user?.market?.country || user?.preferences?.country || "")
);

const resolveUserEmail = (user = null) => {
  if (typeof user?.email !== "string") {
    return "";
  }
  return user.email.trim();
};

const buildDonationUrlWithPrefilledEmail = (baseUrl, email) => {
  if (!baseUrl || !email) {
    return baseUrl;
  }

  try {
    const url = new URL(baseUrl);
    url.searchParams.set("prefilled_email", email);
    return url.toString();
  } catch {
    const separator = baseUrl.includes("?") ? "&" : "?";
    return `${baseUrl}${separator}prefilled_email=${encodeURIComponent(email)}`;
  }
};

export const resolveDonationCurrency = ({ user = null, pricingContext = null } = {}) => {
  const selectedCountry = normalizeCountryCode(localStorageService.getSelectedCountryCode());
  const detectedCountry = normalizeCountryCode(localStorageService.getDetectedCountryCode());
  const userCountry = resolveUserCountry(user);
  const countryForPricing = userCountry || selectedCountry || detectedCountry || "US";
  const resolvedCurrency = normalizeCurrencyCode(
    resolveEffectiveCurrency(countryForPricing, pricingContext)?.currency
  );

  if (SUPPORTED_DONATION_CURRENCIES.has(resolvedCurrency)) {
    return resolvedCurrency;
  }

  const countryForRegionalFallback = countryForPricing || detectedCountry;
  if (countryForRegionalFallback && EUROPEAN_COUNTRY_CODES.has(countryForRegionalFallback)) {
    return "EUR";
  }

  return "USD";
};

export const resolveDonationConfig = ({ user = null, pricingContext = null } = {}) => {
  const resolvedCurrency = resolveDonationCurrency({ user, pricingContext });
  const donationLinks = getConfiguredDonationLinks();
  const fallbackUsdLink = donationLinks.USD;
  const primaryLink = donationLinks[resolvedCurrency];
  const fallbackLink = resolvedCurrency === "USD" ? primaryLink : fallbackUsdLink;
  const resolvedBaseUrl = primaryLink || fallbackLink || "";
  const userEmail = resolveUserEmail(user);
  const url = buildDonationUrlWithPrefilledEmail(resolvedBaseUrl, userEmail);
  const targetUrlCurrency = primaryLink
    ? resolvedCurrency
    : (fallbackLink ? "USD" : resolvedCurrency);
  const usedFallbackCurrency = Boolean(primaryLink) ? false : Boolean(fallbackLink);

  return {
    url,
    currency: targetUrlCurrency,
    resolvedCurrency,
    targetUrlCurrency,
    usedFallbackCurrency,
    isVisible: Boolean(url),
  };
};
